import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import API from '../../Helper/server/API';
import { _endPoints, getpaymenttype } from '../../Helper/links';
import { AR_MONTHS, STRINGS, formatDayLabel, pickColor } from '../utils/constants';

// ─────────────────────────────────────────────────────────────────────────────
// Status enum — drives which view the dashboard renders.
// ─────────────────────────────────────────────────────────────────────────────
export const STATUS = {
  IDLE: 'idle',          // No data fetched yet, awaiting first request.
  LOADING: 'loading',    // Request in-flight.
  ERROR: 'error',        // Request failed.
  EMPTY: 'empty',        // Request succeeded but the filter produced no rows.
  READY: 'ready',        // Data is available and renderable.
};

const ISO = (m) => m.format('YYYY-MM-DD');
const todayMoment = () => moment().startOf('day');
const todayISO = () => ISO(todayMoment());

// ── Resolve [startDate, endDate] for a (dateRange, anchorDate) tuple ────────-
// Centralised so navigation arrows and date-range switches share one source.
const resolveRange = (dateRange, anchorISO) => {
  if (!anchorISO) return { startDate: '', endDate: '' };
  const a = moment(anchorISO).startOf('day');
  if (dateRange === 'week') {
    const s = a.clone().subtract(6, 'days');
    return { startDate: ISO(s), endDate: ISO(a) };
  }
  if (dateRange === 'month') {
    return { startDate: ISO(a.clone().startOf('month')), endDate: ISO(a.clone().endOf('month')) };
  }
  if (dateRange === 'year') {
    return {
      startDate: ISO(a.clone().subtract(11, 'months').startOf('month')),
      endDate: ISO(a),
    };
  }
  return { startDate: '', endDate: '' };
};

// ── Human label for the current selection ───────────────────────────────────
const formatPeriodLabel = ({ dateRange, startDate, endDate }) => {
  if (!startDate || !endDate) return '';
  const s = moment(startDate);
  const e = moment(endDate);
  if (dateRange === 'week') {
    const sameMonth = s.month() === e.month() && s.year() === e.year();
    if (sameMonth) {
      return `${s.date()} - ${e.date()} ${AR_MONTHS[e.month()]} ${e.year()}`;
    }
    return `${s.date()} ${AR_MONTHS[s.month()]} - ${e.date()} ${AR_MONTHS[e.month()]} ${e.year()}`;
  }
  if (dateRange === 'month') return `${AR_MONTHS[s.month()]} ${s.year()}`;
  if (dateRange === 'year') return `${AR_MONTHS[s.month()]} ${s.year()} - ${AR_MONTHS[e.month()]} ${e.year()}`;
  return `${startDate} ← ${endDate}`;
};

// ─── Builds the chart-ready shape from the raw API response ─────────────────
// The shape consumed by every widget — never touched outside this hook.
const transformResponse = ({ response, selectedServices, dateRange, startDate, endDate }) => {
  const paymentsRaw = response.payments || [];
  const paymentTypesFromServer = response.payment_types || [];

  // Index payment rows by their period key (date / month).
  const periodIndex = {};
  paymentsRaw.forEach((row) => {
    const key = row.date || row.month || row.period;
    if (key) periodIndex[key] = row;
  });

  const labels = [];
  const fullDateLabels = [];
  const periodKeys = [];
  const dateMoments = [];
  const revenueArr = [];
  const reservationsArr = [];
  const visitorsArr = [];

  if (dateRange === 'year') {
    paymentsRaw.forEach((row) => {
      const m = moment(row.month || row.date || '', 'YYYY-MM');
      labels.push(AR_MONTHS[m.month()]);
      fullDateLabels.push(`${AR_MONTHS[m.month()]} ${m.year()}`);
      periodKeys.push(row.month || row.date || '');
      dateMoments.push(m);
      revenueArr.push(Number(row.amount) || 0);
      reservationsArr.push(Number(row.count_payments) || 0);
      visitorsArr.push(Number(row.number_people) || 0);
    });
  } else if (dateRange === 'month') {
    const anchor = moment(startDate);
    const year = anchor.year();
    const month = anchor.month();
    const daysInMonth = anchor.daysInMonth();
    for (let day = 1; day <= daysInMonth; day += 1) {
      const m = moment({ year, month, day });
      const dateStr = ISO(m);
      labels.push(formatDayLabel(m));
      fullDateLabels.push(`${day} ${AR_MONTHS[month]} ${year}`);
      periodKeys.push(dateStr);
      dateMoments.push(m);
      const row = periodIndex[dateStr];
      revenueArr.push(Number(row?.amount) || 0);
      reservationsArr.push(Number(row?.count_payments) || 0);
      visitorsArr.push(Number(row?.number_people) || 0);
    }
  } else {
    // week / custom — iterate every day in the inclusive range.
    const cur = moment(startDate).clone();
    const endM = moment(endDate);
    while (cur.isSameOrBefore(endM, 'day')) {
      const m = cur.clone();
      const dateStr = ISO(m);
      labels.push(formatDayLabel(m));
      fullDateLabels.push(`${m.date()} ${AR_MONTHS[m.month()]} ${m.year()}`);
      periodKeys.push(dateStr);
      dateMoments.push(m);
      const row = periodIndex[dateStr];
      revenueArr.push(Number(row?.amount) || 0);
      reservationsArr.push(Number(row?.count_payments) || 0);
      visitorsArr.push(Number(row?.number_people) || 0);
      cur.add(1, 'day');
    }
  }

  // ── Per-service breakdown built from the same period keys ─────────────────
  const serviceVisitors = {};
  const serviceRevenue = {};
  selectedServices.forEach((srv) => {
    serviceVisitors[srv.ID_service] = [];
    serviceRevenue[srv.ID_service] = [];
  });

  // ── Per-payment breakdown built from the same period keys ─────────────────
  const paymentMethodsDaily = {};
  paymentTypesFromServer.forEach((pt) => {
    paymentMethodsDaily[pt.id_payment_type] = [];
  });

  periodKeys.forEach((key) => {
    const row = periodIndex[key];
    selectedServices.forEach((srv) => {
      const srvData = row?.services?.[srv.ID_service];
      serviceVisitors[srv.ID_service].push(Number(srvData?.number_people) || 0);
      serviceRevenue[srv.ID_service].push(Number(srvData?.amount) || 0);
    });
    paymentTypesFromServer.forEach((pt) => {
      const amount = Number(row?.methods?.[pt.id_payment_type]) || 0;
      paymentMethodsDaily[pt.id_payment_type].push(amount);
    });
  });

  // ── Service summary (totals per service across the range) ─────────────────
  const serviceSummary = selectedServices.map((srv, idx) => {
    const totalRevenue = (serviceRevenue[srv.ID_service] || []).reduce((a, b) => a + b, 0);
    const totalVisitors = (serviceVisitors[srv.ID_service] || []).reduce((a, b) => a + b, 0);
    return {
      id: srv.ID_service,
      key: String(srv.ID_service),
      name: srv.service_name_ar || srv.service_name_en || `#${srv.ID_service}`,
      color: pickColor(idx),
      totalRevenue,
      totalVisitors,
    };
  });

  // ── Payment-method breakdown directly from server totals ──────────────────
  const totals = response.totals || {};
  const totalRevenue = Number(totals.amount) || 0;
  const totalReservations = Number(totals.count_payments) || 0;
  const totalVisitors = Number(totals.number_people) || 0;
  const methodsNamed = totals.methods_named || {};

  const paymentBreakdown = paymentTypesFromServer
    .map((pt, idx) => {
      const amount = Number(methodsNamed[pt.name_payment_type]) || 0;
      const percentage = totalRevenue > 0 ? Math.round((amount / totalRevenue) * 100) : 0;
      return {
        id: pt.id_payment_type,
        name: pt.name_payment_type,
        amount,
        percentage,
        color: pickColor(idx + selectedServices.length),
      };
    })
    .filter((p) => p.amount > 0);

  // ── Peak day (max revenue) ────────────────────────────────────────────────
  let peakDayName = labels[0] || '';
  let peakRev = -1;
  revenueArr.forEach((rev, idx) => {
    if (rev > peakRev) {
      peakRev = rev;
      peakDayName = fullDateLabels[idx] || labels[idx] || peakDayName;
    }
  });

  // ── "Today" marker index — used by the chart to highlight current day ────-
  const todayKey = todayISO();
  const todayIndex = periodKeys.indexOf(todayKey);

  // ── Service ranking ───────────────────────────────────────────────────────
  const ranked = [...serviceSummary].sort((a, b) => b.totalRevenue - a.totalRevenue);
  const topService = ranked[0] || null;
  const leastService = ranked.length > 1 ? ranked[ranked.length - 1] : null;
  const popularByVisitors = [...serviceSummary].sort((a, b) => b.totalVisitors - a.totalVisitors)[0] || null;

  // ── Derived KPI helpers ───────────────────────────────────────────────────
  const periodCount = labels.length;
  const avgRevenue = totalReservations > 0 ? Math.round(totalRevenue / totalReservations) : 0;
  const avgDailyRevenue = periodCount > 0 ? Math.round(totalRevenue / periodCount) : 0;

  const stats = response.stats || {};
  const activatedBracelets = Number(stats.activated_bracelets) || 0;

  return {
    labels,
    fullDateLabels,
    periodKeys,
    periodCount,
    todayIndex,

    revenueArr,
    reservationsArr,
    visitorsArr,

    serviceVisitors,
    serviceRevenue,
    serviceSummary,

    paymentBreakdown,
    paymentMethodsDaily,

    totalRevenue,
    totalReservations,
    totalVisitors,
    avgRevenue,
    avgDailyRevenue,
    activatedBracelets,

    topService,
    leastService,
    popularByVisitors,
    peakDayName,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook: useDashboardData
// Owns filters, available services / payment types, and fetched analytics.
// Supports anchor-based week/month/year navigation with auto-refetch.
// ─────────────────────────────────────────────────────────────────────────────
const useDashboardData = ({ onWarn, onError } = {}) => {
  const [dateRange, setDateRange] = useState('week');

  // Anchor drives week/month/year selection. Reset to today whenever the
  // user switches dateRange so we always start from the current period.
  const [anchorDate, setAnchorDate] = useState(todayISO);

  const initialRange = useMemo(() => resolveRange('week', todayISO()), []);
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);

  // ── Option lists ──────────────────────────────────────────────────────────
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  const [paymentTypes, setPaymentTypes] = useState([]);
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState([]);
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false);

  // ── Fetched analytics ─────────────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState('');

  // ── Refs that drive auto-refetch without retriggering the effect ─────────-
  const hasFetchedOnceRef = useRef(false);
  const fetchDataRef = useRef(null);

  // ── Date-range switch resets anchor and recomputes the window ────────────-
  useEffect(() => {
    if (dateRange === 'custom') return;
    const today = todayISO();
    setAnchorDate(today);
    const r = resolveRange(dateRange, today);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
  }, [dateRange]);

  // ── Anchor change inside a preset recomputes the window ──────────────────-
  useEffect(() => {
    if (dateRange === 'custom') return;
    const r = resolveRange(dateRange, anchorDate);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
  }, [anchorDate, dateRange]);

  // ── Load option lists once ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const loadServices = async () => {
      setLoadingServices(true);
      try {
        const response = await API.get(_endPoints.reservations.services.get_all);
        if (cancelled) return;
        if (response?.success === 1 && Array.isArray(response.data)) {
          setServices(response.data);
          setSelectedServices(response.data);
        }
      } finally {
        if (!cancelled) setLoadingServices(false);
      }
    };

    const loadPaymentTypes = async () => {
      setLoadingPaymentTypes(true);
      try {
        const response = await API.get(getpaymenttype);
        if (cancelled) return;
        if (response?.success === 1 && Array.isArray(response.data)) {
          setPaymentTypes(response.data);
          setSelectedPaymentTypes(response.data);
        }
      } finally {
        if (!cancelled) setLoadingPaymentTypes(false);
      }
    };

    loadServices();
    loadPaymentTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Fetch dashboard analytics ─────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      onWarn?.(STRINGS.selectDateRange);
      return;
    }

    const selectedServiceIds = selectedServices
      .map((s) => s.ID_service)
      .filter(Boolean);
    const selectedPaymentTypeIds = selectedPaymentTypes
      .map((p) => p.id_payment_type)
      .filter(Boolean);

    if (selectedServiceIds.length === 0) {
      onWarn?.(STRINGS.selectAtLeastOneService);
      return;
    }
    if (selectedPaymentTypeIds.length === 0) {
      onWarn?.(STRINGS.selectAtLeastOnePayment);
      return;
    }

    const payload = {
      startDate,
      endDate,
      dateRange,
      users: [0],
      apartments: [0],
      services: selectedServiceIds,
      payment_types: selectedPaymentTypeIds,
      payment_by_customer: false,
    };

    // Mark "fetched at least once" so navigation can auto-refetch from now on.
    hasFetchedOnceRef.current = true;

    setStatus(STATUS.LOADING);
    setData(null);
    setErrorMessage('');

    try {
      const response = await API.post(_endPoints.reservations.services.dashboard, payload);

      if (response?.success === 1) {
        const transformed = transformResponse({
          response,
          selectedServices,
          dateRange,
          startDate,
          endDate,
        });

        const hasAnyActivity =
          transformed.totalRevenue > 0 ||
          transformed.totalReservations > 0 ||
          transformed.totalVisitors > 0;

        setData(transformed);
        setStatus(hasAnyActivity ? STATUS.READY : STATUS.EMPTY);
      } else {
        const msg = response?.msg || STRINGS.connectionErrorDetail;
        setErrorMessage(msg);
        setStatus(STATUS.ERROR);
        onError?.(msg);
      }
    } catch (err) {
      const msg = err?.message || STRINGS.connectionErrorDetail;
      setErrorMessage(msg);
      setStatus(STATUS.ERROR);
      onError?.(msg);
    }
  }, [startDate, endDate, dateRange, selectedServices, selectedPaymentTypes, onWarn, onError]);

  // Keep the live fetchData accessible to the auto-refetch effect without
  // pulling its identity into the dep array (which would loop).
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // ── Auto-refetch on navigation (only after the first manual fetch) ───────-
  useEffect(() => {
    if (!hasFetchedOnceRef.current) return;
    if (!startDate || !endDate) return;
    fetchDataRef.current?.();
    // We deliberately exclude fetchDataRef.current — its identity is stable
    // through the ref and we only want to refetch when the window changes.
  }, [startDate, endDate]);

  // ── Navigation actions ────────────────────────────────────────────────────
  const shiftAnchor = useCallback((direction) => {
    if (dateRange === 'custom') return;
    setAnchorDate((prev) => {
      const m = moment(prev || todayISO());
      const unit = dateRange === 'year' ? 'year' : dateRange === 'month' ? 'month' : 'week';
      return ISO(direction === 'next' ? m.add(1, unit) : m.subtract(1, unit));
    });
  }, [dateRange]);

  const goToPrevious = useCallback(() => shiftAnchor('prev'), [shiftAnchor]);
  const goToNext = useCallback(() => shiftAnchor('next'), [shiftAnchor]);
  const goToToday = useCallback(() => {
    if (dateRange === 'custom') return;
    setAnchorDate(todayISO());
  }, [dateRange]);

  // "Next" is disabled once the anchor has reached the current period —
  // stepping further would put us in the future.
  const canGoNext = useMemo(() => {
    if (dateRange === 'custom') return false;
    const a = moment(anchorDate);
    const today = todayMoment();
    if (dateRange === 'year') return a.isBefore(today, 'year');
    if (dateRange === 'month') return a.isBefore(today, 'month');
    return a.isBefore(today, 'day');
  }, [anchorDate, dateRange]);

  const isAtCurrentPeriod = useMemo(() => {
    if (dateRange === 'custom') return false;
    const a = moment(anchorDate);
    if (dateRange === 'year') return a.isSame(todayMoment(), 'year');
    if (dateRange === 'month') return a.isSame(todayMoment(), 'month');
    return a.isSame(todayMoment(), 'day');
  }, [anchorDate, dateRange]);

  const periodLabel = useMemo(
    () => formatPeriodLabel({ dateRange, startDate, endDate }),
    [dateRange, startDate, endDate]
  );

  // ── Status helpers ────────────────────────────────────────────────────────
  const isLoading = status === STATUS.LOADING;
  const isReady = status === STATUS.READY;
  const isEmpty = status === STATUS.EMPTY;
  const isError = status === STATUS.ERROR;
  const isIdle = status === STATUS.IDLE;

  return useMemo(
    () => ({
      // Filters
      dateRange,
      setDateRange,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      anchorDate,

      // Navigation
      goToPrevious,
      goToNext,
      goToToday,
      canGoNext,
      isAtCurrentPeriod,
      periodLabel,

      // Option lists
      services,
      selectedServices,
      setSelectedServices,
      loadingServices,
      paymentTypes,
      selectedPaymentTypes,
      setSelectedPaymentTypes,
      loadingPaymentTypes,

      // Data + status
      data,
      status,
      errorMessage,
      isLoading,
      isReady,
      isEmpty,
      isError,
      isIdle,

      fetchData,
    }),
    [
      dateRange,
      startDate,
      endDate,
      anchorDate,
      goToPrevious,
      goToNext,
      goToToday,
      canGoNext,
      isAtCurrentPeriod,
      periodLabel,
      services,
      selectedServices,
      loadingServices,
      paymentTypes,
      selectedPaymentTypes,
      loadingPaymentTypes,
      data,
      status,
      errorMessage,
      isLoading,
      isReady,
      isEmpty,
      isError,
      isIdle,
      fetchData,
    ]
  );
};

export { todayISO };
export default useDashboardData;
